/**
 * Template Customization Form
 *
 * Dialog form for creating/editing template customizations.
 * Provides UI for root wrapper, slot wrappers, and additions.
 */

import { useState, useEffect } from 'react';
import { X, Save, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import type { TemplateCustomization } from '@onecreator/shared';
import { api } from '../../api';

interface TemplateCustomizationFormProps {
  projectId: string;
  templateId: string | null; // null for new customization
  existingCustomization?: TemplateCustomization;
  availableTemplates: Array<{ id: string; label: string; slots: string[] }>;
  uncustomizedTemplates: Array<{ id: string; label: string; slots: string[] }>;
  onSave: (templateId: string, customization: TemplateCustomization) => Promise<void>;
  onCancel: () => void;
}

interface ValidationResult {
  valid: boolean;
  errors: Array<{ field: string; message: string }>;
  warnings: Array<{ field: string; message: string; severity: string }>;
}

export function TemplateCustomizationForm({
  projectId,
  templateId,
  existingCustomization,
  availableTemplates,
  uncustomizedTemplates,
  onSave,
  onCancel,
}: TemplateCustomizationFormProps) {
  const isEditing = !!templateId;
  const [selectedTemplateId, setSelectedTemplateId] = useState(templateId || '');
  const [customization, setCustomization] = useState<TemplateCustomization>(
    existingCustomization || {
      extends: templateId || '',
      rootWrapper: undefined,
      slotWrappers: undefined,
      additions: undefined,
    }
  );

  const [isSaving, setIsSaving] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Get selected template info
  const selectedTemplate = availableTemplates.find((t) => t.id === selectedTemplateId);

  // Update customization.extends when template selection changes
  useEffect(() => {
    if (selectedTemplateId) {
      setCustomization((prev) => ({
        ...prev,
        extends: selectedTemplateId,
      }));
    }
  }, [selectedTemplateId]);

  // Validate on change (debounced)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (selectedTemplateId && customization.extends) {
        validateCustomization();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [customization, selectedTemplateId]);

  const validateCustomization = async () => {
    try {
      const response = await api.post(
        `/projects/${projectId}/template-customizations/validate`,
        customization
      );
      setValidationResult(response.data.data);
    } catch (err) {
      console.error('Validation error:', err);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplateId) {
      setSaveError('Seleziona un template');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      await onSave(selectedTemplateId, customization);
    } catch (err: any) {
      setSaveError(err.message || 'Errore durante il salvataggio');
    } finally {
      setIsSaving(false);
    }
  };

  const updateRootWrapper = (field: string, value: any) => {
    setCustomization((prev) => ({
      ...prev,
      rootWrapper: {
        ...prev.rootWrapper,
        [field]: value || undefined,
      },
    }));
  };

  const addSlotWrapper = (slotId: string) => {
    setCustomization((prev) => ({
      ...prev,
      slotWrappers: {
        ...prev.slotWrappers,
        [slotId]: {
          before: '',
          after: '',
          className: '',
        },
      },
    }));
  };

  const updateSlotWrapper = (slotId: string, field: string, value: any) => {
    setCustomization((prev) => ({
      ...prev,
      slotWrappers: {
        ...prev.slotWrappers,
        [slotId]: {
          ...prev.slotWrappers?.[slotId],
          [field]: value || undefined,
        },
      },
    }));
  };

  const removeSlotWrapper = (slotId: string) => {
    setCustomization((prev) => {
      const newSlotWrappers = { ...prev.slotWrappers };
      delete newSlotWrappers[slotId];
      return {
        ...prev,
        slotWrappers: Object.keys(newSlotWrappers).length > 0 ? newSlotWrappers : undefined,
      };
    });
  };

  const updateAddition = (position: string, value: string) => {
    setCustomization((prev) => {
      const newAdditions = { ...prev.additions };
      if (value) {
        newAdditions[position] = value;
      } else {
        delete newAdditions[position];
      }
      return {
        ...prev,
        additions: Object.keys(newAdditions).length > 0 ? newAdditions : undefined,
      };
    });
  };

  const canSave = selectedTemplateId && (!validationResult || validationResult.valid);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {isEditing ? 'Modifica Customizzazione' : 'Nuova Customizzazione'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {isEditing
                ? `Template: ${selectedTemplateId}`
                : 'Seleziona un template e definisci le personalizzazioni'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Template Selection */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template da Customizzare *
              </label>
              <select
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              >
                <option value="">-- Seleziona un template --</option>
                {uncustomizedTemplates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.label} ({template.id})
                  </option>
                ))}
              </select>
              {selectedTemplate && (
                <p className="mt-1 text-xs text-gray-500">
                  Slots disponibili: {selectedTemplate.slots.join(', ')}
                </p>
              )}
            </div>
          )}

          {/* Info Banner */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start space-x-2">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm text-blue-800">
              <p>
                Le customizzazioni vengono applicate al template base del catalogo. L'HTML
                inserito viene automaticamente sanificato per prevenire XSS.
              </p>
            </div>
          </div>

          {/* Root Wrapper Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Root Wrapper</h3>
            <p className="text-xs text-gray-600 mb-4">
              Personalizza il container principale del template
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CSS Classes
                </label>
                <input
                  type="text"
                  value={customization.rootWrapper?.className || ''}
                  onChange={(e) => updateRootWrapper('className', e.target.value)}
                  placeholder="es: museo-theme gradient-bg dark-mode"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm font-mono"
                />
              </div>
            </div>
          </div>

          {/* Slot Wrappers Section */}
          {selectedTemplate && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Slot Wrappers</h3>
                  <p className="text-xs text-gray-600 mt-1">
                    Aggiungi HTML wrapper specifici per ogni slot
                  </p>
                </div>
              </div>

              {selectedTemplate.slots.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Nessuno slot disponibile</p>
              ) : (
                <div className="space-y-4">
                  {selectedTemplate.slots.map((slotId) => {
                    const hasWrapper = !!customization.slotWrappers?.[slotId];

                    return (
                      <div
                        key={slotId}
                        className="bg-white border border-gray-200 rounded-lg p-3"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-900 font-mono">
                            {slotId}
                          </span>
                          {hasWrapper ? (
                            <button
                              onClick={() => removeSlotWrapper(slotId)}
                              className="text-xs text-red-600 hover:text-red-700"
                            >
                              Rimuovi wrapper
                            </button>
                          ) : (
                            <button
                              onClick={() => addSlotWrapper(slotId)}
                              className="text-xs text-primary-600 hover:text-primary-700"
                            >
                              Aggiungi wrapper
                            </button>
                          )}
                        </div>

                        {hasWrapper && (
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                HTML Before
                              </label>
                              <textarea
                                value={customization.slotWrappers![slotId].before || ''}
                                onChange={(e) =>
                                  updateSlotWrapper(slotId, 'before', e.target.value)
                                }
                                placeholder='es: <div class="slot-wrapper">'
                                rows={2}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                HTML After
                              </label>
                              <textarea
                                value={customization.slotWrappers![slotId].after || ''}
                                onChange={(e) =>
                                  updateSlotWrapper(slotId, 'after', e.target.value)
                                }
                                placeholder="es: </div>"
                                rows={2}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-600 mb-1">
                                CSS Classes
                              </label>
                              <input
                                type="text"
                                value={customization.slotWrappers![slotId].className || ''}
                                onChange={(e) =>
                                  updateSlotWrapper(slotId, 'className', e.target.value)
                                }
                                placeholder="es: animated fade-in"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-primary-500"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Additions Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Additions</h3>
            <p className="text-xs text-gray-600 mb-4">
              Aggiungi HTML in posizioni specifiche del template
            </p>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Before All (prima di tutti gli slot)
                </label>
                <textarea
                  value={customization.additions?.beforeAll || ''}
                  onChange={(e) => updateAddition('beforeAll', e.target.value)}
                  placeholder='es: <div class="page-header">Header personalizzato</div>'
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  After All (dopo tutti gli slot)
                </label>
                <textarea
                  value={customization.additions?.afterAll || ''}
                  onChange={(e) => updateAddition('afterAll', e.target.value)}
                  placeholder='es: <footer class="custom-footer">Footer</footer>'
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          {/* Validation Results */}
          {validationResult && !validationResult.valid && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800">Errori di Validazione</p>
                  <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                    {validationResult.errors.map((error, idx) => (
                      <li key={idx}>
                        {error.field}: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {validationResult && validationResult.valid && validationResult.warnings.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">Warnings</p>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                    {validationResult.warnings.map((warning, idx) => (
                      <li key={idx}>
                        {warning.field}: {warning.message}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {validationResult && validationResult.valid && validationResult.warnings.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center space-x-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm text-green-800">Customizzazione valida!</p>
            </div>
          )}

          {/* Save Error */}
          {saveError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{saveError}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button onClick={onCancel} className="btn-secondary" disabled={isSaving}>
            Annulla
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || isSaving}
            className="btn-primary flex items-center space-x-2"
          >
            <Save className="w-4 h-4" />
            <span>{isSaving ? 'Salvataggio...' : 'Salva Customizzazione'}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
